// Streaming chat-turn client.
//
// A chat turn is special: unlike the JSON endpoints, the server streams the
// reply back as NDJSON (one JSON object per line). The event vocabulary:
//
//   { "type": "chunk",  "text": "..." }                 zero or more reply pieces
//   { "type": "done",   "block_complete": bool, ... }   success terminal
//   { "type": "ended",  "reason": "..." }               graceful budget end (session over)
//   { "type": "error",  "message": "..." }              this attempt failed; retry is safe
//
// Why we always read to the end of the stream: the server settles the wallet
// charge INSIDE the stream, just before it sends "done". A client that hangs up
// early could leave a turn half-finished. So this reader drains every byte.
//
// Retry safety: each turn carries a turn_id. Re-sending the SAME turn_id after a
// failure is safe (the server either replays the finished turn without charging
// again, or, if the failed attempt charged nothing, lets a fresh attempt run).

import { API_BASE, SessionApiError } from "../api/client";
import type { SessionHandle } from "./sessionDriver";

export type ChatEvent =
  | { type: "chunk"; text: string }
  | { type: "done"; block_complete: boolean; [k: string]: unknown }
  | { type: "ended"; reason: string }
  | { type: "error"; message: string }
  // A scripted A/B step: the server returns two candidate replies (no model
  // call) so the client opens the comparison dialog and commits the choice.
  | { type: "ab"; test_type?: string; option_a?: string; option_b?: string; reply_delay_ms?: number };

export interface ChatTurnInput {
  turnId: string;
  message: string;
  end?: boolean;
  forceWebSearch?: boolean;
}

export async function streamChatTurn(
  h: SessionHandle,
  input: ChatTurnInput,
  onEvent: (e: ChatEvent) => void,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/sessions/${h.sessionId}/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": h.sessionToken,
      },
      body: JSON.stringify({
        turn_id: input.turnId,
        message: input.message,
        end: input.end ?? false,
        force_web_search: input.forceWebSearch ?? false,
      }),
    });
  } catch {
    throw new SessionApiError(0, "Network error. Please check your connection.");
  }

  // A non-streaming failure (auth, session not active, etc.) comes back as the
  // normal C2 JSON envelope, not a stream.
  if (!res.ok || !res.body) {
    let payload: { message?: string; code?: string; request_id?: string } = {};
    try {
      payload = await res.json();
    } catch {
      // non-JSON error body; fall through with defaults
    }
    throw new SessionApiError(
      res.status,
      payload.message ?? `Request failed (${res.status})`,
      payload.code,
      payload.request_id,
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushLine = (raw: string) => {
    const line = raw.trim();
    if (!line) return;
    let evt: ChatEvent;
    try {
      evt = JSON.parse(line) as ChatEvent;
    } catch {
      return; // ignore a malformed line rather than crashing the turn
    }
    onEvent(evt);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf("\n")) >= 0) {
      flushLine(buffer.slice(0, nl));
      buffer = buffer.slice(nl + 1);
    }
  }
  flushLine(buffer); // a trailing line with no newline
}
